import { useEffect, useState } from 'react';
import styled from 'styled-components';
import { ReactComponent as EmptyGraphic } from '@elementary/common-ui/assets/error-graphic.svg';
import { FlexColumn } from '@elementary/common-ui/components';
import { Typography } from '@mui/material';
import { useErrorBoundary } from 'react-error-boundary';
import { useLocation } from 'react-router-dom';
import useSearchParams from '@elementary/common-ui/hooks/useSearchParams';
import useSupportChat from 'shared/hooks/useSupportChat';
import useViewErrorsTracking from '@elementary/common-ui/hooks/tracking/useViewErrorsTracking';
import { extractApiContext } from '@elementary/common-ui/utils/errorTracking';

const Link = styled.a`
  color: ${({ theme }) => theme.palette.common.gray2};
`;

const EmptyGraphicStyled = styled(EmptyGraphic)`
  margin-bottom: 50px;
`;

const Message = styled(Typography)`
  white-space: pre-wrap;
  text-align: center;
  color: ${({ theme }) => theme.palette.common.gray2};
`;

const ClickableText = styled(Message).attrs(() => ({
  component: 'span',
}))`
  cursor: pointer;
  text-decoration: underline;
`;

type Props = {
  error: Error;
  errorName?: 'ApiError' | 'ReactRenderingError';
  page?: string;
};

const ErrorPage = ({ error, errorName, page }: Props) => {
  const [loaded, setLoaded] = useState(false);
  const { resetBoundary } = useErrorBoundary();
  const { pathname } = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const supportChat = useSupportChat();

  const apiContext =
    errorName === 'ApiError' ? extractApiContext(error) : undefined;

  useViewErrorsTracking({
    error,
    errorName,
    context: {
      component: 'FrontendErrorPage',
      page,
      previousRoute: document.referrer,
      appState: {
        searchParams: Object.fromEntries(searchParams.entries()),
        currentRoute: pathname,
      },
      ...(apiContext && { api: apiContext }),
      tags: ['error-boundary', 'frontend-app'],
    },
  });

  useEffect(() => {
    if (loaded) {
      resetBoundary();
      setSearchParams();
    }
    setLoaded(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  return (
    <FlexColumn
      sx={{
        height: '100%',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 2,
      }}
    >
      <EmptyGraphicStyled />
      <Typography sx={{ fontSize: '34px', fontWeight: 'bold' }}>
        Oops..
      </Typography>
      <Message>
        Sorry, something went wrong.
        <br />
        Try <Link href=".">refreshing the page</Link> or{' '}
        <ClickableText onClick={supportChat.show}>
          reach out to us
        </ClickableText>
      </Message>
    </FlexColumn>
  );
};

export default ErrorPage;
